<template lang="html">
  <v-dialog 
    v-model="dialog" 
    persistent
    max-width="1300"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        v-show="$hasPermissionTo(['create employees'])"
        color="hrissecondary"
        class="white--text px-10 mx-1"
        v-on="on"
      >
        Create Employee
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="User Creation"
      text="Create and configure user"
      class="mb-0"
    >
      <v-container
        v-if="loading"
        style="height: 150px;"
      >
        <v-row
          class="fill-height"
          align="center" 
          justify="center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </v-row>     
      </v-container>
      <v-container v-else>
        <v-stepper v-model="step">
          <v-stepper-header>
            <template v-for="(stepperHeader,i) in stepperHeaders">
              <v-stepper-step
                :key="`${i}-key`"
                :step="i+1"
                :complete="i+1 < step"
              >
                {{ stepperHeader.name }}
              </v-stepper-step>
              <v-divider
                v-if="i !== stepperHeaders"
                :key="i"
              />
            </template>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content step="1">
              <employee-form-create
                ref="employeeFormDetail"
                @close-dialog="closeDialog"
                @next-step="nextStep"
                @save-as-draft="saveAsDraft"
              />
            </v-stepper-content>

            <v-stepper-content step="2">
              <employee-report-to-form-create
                ref="reportToFormDetail"
                :step="step"
                @close-dialog="closeDialog"
                @previous-step="previousStep"
                @next-step="nextStep"
                @save-as-draft="saveAsDraft"
              />
            </v-stepper-content>

            <v-stepper-content step="3">
              <user-form-create
                ref="userFormDetail"
                @close-dialog="closeDialog"
                @previous-step="previousStep"
                @save-as-draft="saveAsDraft"
                @submit-form="submitForm"
              />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-container>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    components: {
      UserFormCreate: () => import('@/components/user/FormCreate'),
      EmployeeFormCreate: () => import('@/components/employee/FormCreate'),
      EmployeeReportToFormCreate: () => import('@/components/employee/report-to/FormCreate'),
    },
    data () {
      return {
        step: 1,
        stepperHeaders: [
          { 'name': 'Employee' },
          { 'name': 'Employee Report To' },
          { 'name': 'User' },
        ],
        dialog: false,
        loading: false,
      }
    },
    watch: {
      dialog: function (val) {
        if (!val) return this.$resetData()

        this.step = 1
      },
    },
    methods: {
      closeDialog () {
        this.$refs.employeeFormDetail.resetData()
        this.$refs.reportToFormDetail.resetData()
        this.$refs.userFormDetail.resetData()

        this.dialog = false
      },
      nextStep () {
        this.step += 1
      },
      previousStep () {
        this.step -= 1
      },
      submitForm () {
        this.$refs.employeeFormDetail.$validator.validateAll().then((result) => {
          if (!result) {
            this.step = 1
          } else {
            this.$refs.userFormDetail.loadingTrue()

            if (!this.$refs.employeeFormDetail.personWithoutEmployee()) {
              this.$api.employees().createEmployeePerson(this.prepareFormData())
                .then((res) => {
                  if (res.status === 201) {
                    this.$snack({ message: 'Employee created successfully' })
                    this.showEmployeeOverview(res.data.employee.employee_uid)
                  }
                  this.$emit('reloadEmployeeList')
                  this.closeDialog()
                })
                .catch((err) => {
                  this.handleErrors(err)
                })
                .then(() => {
                  this.$refs.userFormDetail.loadingFalse()
                })
            } else {
              this.$api.employees().create(this.prepareFormData())
                .then((res) => {
                  if (res.status === 201) {
                    this.$snack({ message: 'Employee created successfully' })
                    this.showEmployeeOverview(res.data.employee.employee_uid)
                  }
                  this.$emit('reloadEmployeeList')
                  this.closeDialog()
                })
                .catch((err) => {
                  this.handleErrors(err)
                })
                .then(() => {
                  this.$refs.userFormDetail.loadingFalse()
                })
            }
          }
        })
      },
      saveAsDraft () {
        this.$api.employees().saveAsDraft(this.prepareFormData())
          .then(() => {
            this.$snack({ message: 'Employee saved to draft' })
            
            this.$emit('reloadEmployeeList')
            this.closeDialog()
          })
          .catch((err) => {
            this.handleErrors(err)
          })
          .then(() => {
            this.$refs.reportToFormDetail.draftProgressing = false
            this.$refs.userFormDetail.draftProgressing = false
            this.$refs.employeeFormDetail.draftProgressing = false            
          })
      },
      showEmployeeOverview (employeeUid) {
        this.$api.employees().showOverview(employeeUid)
          .then(response => {
            this.$emit('openProfile', response.data)
          })
      },
      handleErrors (err) {
        if (err.response.status === 422) {
          if (err.response.data.errors) {
            let errors = err.response.data.errors
            for (const param in errors) {
              this.$snack({ 
                color: 'error',
                message: `${errors[param]}`, 
              })
            }
          } else {
            this.$snack({
              color: 'error',
              message: err.response.data.message,
            })
          }
        } else {
          this.$snack({
            color: 'error',
            message: 'Unable to create employee',
          })
        }
      },
      prepareFormData () {
        let employeeFormData = this.$refs.employeeFormDetail.employeeFormData()
        let personWithoutEmployeeFormData = this.$refs.employeeFormDetail.getFormData()
        let reportToFormData = this.$refs.reportToFormDetail.getFormData()
        let userFormData = this.$refs.userFormDetail.getFormData()

        if (!this.$refs.employeeFormDetail.personWithoutEmployee()) {
          return {
            employeeData: personWithoutEmployeeFormData,
            reportToData: reportToFormData,
            userData: userFormData,
          }
        } else {
          return {
            employeeData: employeeFormData,
            reportToData: reportToFormData,
            userData: userFormData,
          }
        }
      },
    },
  }
</script>
